import React, { useMemo, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { categoryIcons } from "../Constants/categoryIcons";
import { createCategoryCustomIcon } from "../mapUtils.js";
import ImageSlider from "./ImageSlider";
import { FaUser, FaEnvelope, FaPhone, FaGlobe, FaMapMarkerAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Component to fit map bounds to markers
const FitBounds = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    if (markers && markers.length > 0) {
      const validMarkers = markers.filter((item) => {
        const lat = item.latitude || item.lat;
        const lng = item.longitude || item.lng || item.lon;
        return lat && lng;
      });

      if (validMarkers.length > 0) {
        const bounds = L.latLngBounds(
          validMarkers.map((item) => [
            item.latitude || item.lat,
            item.longitude || item.lng || item.lon,
          ])
        );

        // Add padding to bounds for better view
        map.fitBounds(bounds, {
          padding: [20, 20],
          maxZoom: 18,
        });
      }
    }
  }, [markers, map]);

  return null;
};

const StreetMapContainer = ({
  markers = [],
  center,
  zoom = 13,
  scrollWheelZoom = false,
  className = "w-full h-full rounded-b-lg",
  style = { height: "100%", width: "100%" },
  showZoomControl = true,
  zoomControlPosition = "bottomright",
  showZoomControlProp = false,
  tileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
}) => {
  const { t } = useTranslation();

  // Calculate center from markers if not provided
  const mapCenter = useMemo(() => {
    if (center) {
      return typeof center === "function" ? center() : center;
    }

    if (markers?.length > 0) {
      const validMarkers = markers.filter((item) => {
        const lat = item.latitude || item.lat;
        const lng = item.longitude || item.lng || item.lon;
        return lat && lng;
      });

      if (validMarkers.length > 0) {
        const avgLat =
          validMarkers.reduce(
            (sum, item) => sum + (item.latitude || item.lat),
            0
          ) / validMarkers.length;
        const avgLng =
          validMarkers.reduce(
            (sum, item) => sum + (item.longitude || item.lng || item.lon),
            0
          ) / validMarkers.length;
        return [avgLat, avgLng];
      }
    }

    return [51.505, -0.09]; // Default center (London)
  }, [markers, center]);

  return (
    <MapContainer
      center={mapCenter}
      zoom={zoom}
      scrollWheelZoom={scrollWheelZoom}
      className={className}
      style={style}
      zoomControl={showZoomControlProp}
    >
      <TileLayer attribution={tileAttribution} url={tileUrl} />
      {markers && markers.length > 0 && <FitBounds markers={markers} />}
      {markers?.length > 0 &&
        markers.map((item, index) => {
          const lat = item.latitude || item.lat;
          const lng = item.longitude || item.lng || item.lon;
          const categoryId = item.categoryId || item.category_id;

          // Only render marker if both lat and lng are available
          if (lat && lng) {
            const customIcon = createCategoryCustomIcon(
              L,
              categoryIcons,
              categoryId
            );
            return (
              <Marker
                key={index}
                position={[lat, lng]}
                icon={customIcon}
                // eventHandlers={{
                //   mouseover: (e) => e.target.openPopup(),

                // }}
              >
                <Popup>
                  <div className="popup-content max-w-[300px]">
                    <div className="mb-2 flex items-start gap-2">
                      <FaUser className="text-[#1a1a1a] min-w-4 shrink-0" />
                       <span className="font-bold text-[#1a1a1a] min-w-[70px] inline-block shrink-0">
                        {t("title")}:
                      </span>
                        <span className="flex-1 min-w-0 break-words">
                        {item.name || item.title || `Location ${index + 1}`}
                      </span>
                    </div>

                    {item?.email && (
                      <div className="mb-2 flex items-start gap-2">
                        <FaEnvelope className="text-[#1a1a1a] min-w-4 shrink-0 mt-0.5" />
                        <span className="font-bold text-[#1a1a1a] min-w-[70px] inline-block shrink-0">
                          {t("email")}:
                        </span>
                        <a
                          href={`mailto:${item.email}`}
                          className="break-words flex-1 min-w-0"
                        >
                          {item.email}
                        </a>
                      </div>
                    )}
                    {item?.phone && (
                      <div className="mb-2 flex items-center gap-2">
                        <FaPhone className="text-[#1a1a1a] min-w-4 shrink-0" />
                        <span className="font-bold text-[#1a1a1a] min-w-[70px] inline-block">
                          {t("phonenumber")}:
                        </span>
                        <span>{item.phone}</span>
                      </div>
                    )}

                    {item?.address && (
                      <div className="mb-2 flex items-start gap-2">
                        <FaMapMarkerAlt className="text-[#1a1a1a] min-w-4 shrink-0 mt-0.5" />
                        <span className="font-bold text-[#1a1a1a] min-w-[70px] inline-block shrink-0">
                          {t("address")}:
                        </span>
                        <div
                          className="flex-1 break-words max-h-[60px] overflow-y-auto overflow-x-hidden pr-1 min-w-0"
                          style={{
                            scrollbarWidth: "thin",
                            WebkitOverflowScrolling: "touch",
                          }}
                        >
                          <span>{item?.address}</span>
                        </div>
                      </div>
                    )}
                    {item?.website && (
                      <div className="mb-2 flex items-start gap-2">
                        <FaGlobe className="text-[#1a1a1a] min-w-4 shrink-0 mt-0.5" />
                        <span className="font-bold text-[#1a1a1a] min-w-[70px] inline-block shrink-0">
                          {t("website")}:
                        </span>
                        <div
                          className="flex-1 break-words max-h-[60px] overflow-y-auto overflow-x-hidden pr-1 min-w-0"
                          style={{
                            scrollbarWidth: "thin",
                            WebkitOverflowScrolling: "touch",
                          }}
                        >
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item?.website}
                          </a>
                        </div>
                      </div>
                    )}
                    {item?.description && (
                      <div className="mb-2 flex items-start gap-2">
                        <span className="font-bold text-[#1a1a1a] min-w-[90px] inline-block shrink-0 whitespace-nowrap">
                          {t("description")}:
                        </span>

                        <div
                          className="max-h-[80px] overflow-y-auto overflow-x-hidden pr-1 flex-1"
                          style={{
                            scrollbarWidth: "thin",
                            WebkitOverflowScrolling: "touch",
                          }}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      </div>
                    )}

                    {/* Images Section */}
                    <ImageSlider media={item?.media || []} maxImages={8} />
                  </div>
                </Popup>
              </Marker>
            );
          }
          return null;
        })}
      {showZoomControl && <ZoomControl position={zoomControlPosition} />}
    </MapContainer>
  );
};

export default StreetMapContainer;
